import { useState, useEffect, useCallback } from 'react';
import { publicApiClient } from '../api/apiClient';
import { useAuthStore } from '../store/authStore';
import { isRecruiterOrAbove } from '../utils/roleUtils';

interface Company {
  id: number;
  name: string;
  description?: string;
  status: string;
  ownerId?: number;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
}

export const useMyCompany = (enabled: boolean = true) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { role } = useAuthStore();

  const fetchMyCompany = useCallback(async () => {
    // Если хук отключен, не загружаем данные
    if (!enabled) {
      console.log('🔍 useMyCompany: Хук отключен, пропускаем загрузку');
      setCompany(null);
      setLoading(false);
      return;
    }

    // Проверяем, имеет ли пользователь право на доступ к API компаний
    if (!isRecruiterOrAbove(role as any)) {
      console.log('🔍 useMyCompany: Пользователь не имеет права на доступ к API компаний, пропускаем загрузку');
      setCompany(null);
      setLoading(false);
      return;
    }

    // Получаем companyId из текущего пользователя
    const { user } = useAuthStore.getState();
    const companyId = (user as any)?.companyId;
    
    if (!companyId) {
      console.log('🔍 useMyCompany: У пользователя нет companyId, пропускаем загрузку');
      setCompany(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Загружаем информацию о компании пользователя по ID:', companyId);
      const response = await publicApiClient.companies.getCompany(companyId);
      console.log('🔍 Получена информация о компании:', response.data);
      setCompany(response.data as Company);
    } catch (err: any) {
      console.error('❌ Ошибка при загрузке информации о компании:', err);
      setError(err.message || 'Ошибка при загрузке информации о компании');
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, [role, enabled]);

  useEffect(() => {
    fetchMyCompany();
  }, [role, fetchMyCompany]);

  return {
    company,
    loading,
    error,
    refetch: fetchMyCompany
  };
};
